export interface SavedSearchLookupItem {
  id: string | number;
  text?: string;
  parent?: string;
  isGroup?: boolean;
}

export interface SavedSearchChipToken {
  text: string;
  isError: boolean;
}

interface SavedSearchBlock {
  start: number;
  end: number;
  inner: string;
  malformed: boolean;
}

interface ParsedSavedSearchItems {
  ids: string[];
  malformed: boolean;
  executeForm: boolean;
}

const SAVED_SEARCHES_MARKER = /\bsavedSearches\s*:/i;
const STANDALONE_ERROR_TOKEN = /^\(ID\s*:\s*.+\)$/i;
const TRAILING_ID_TOKEN = /\(\s*ID\s*:\s*([^)]+)\)\s*$/i;
const NUMERIC_ID = /^\d+$/;

const stripTrailingSavedSearchId = (raw: string, id: string): string => {
  let label = raw.trim();
  const normalizedId = String(id).trim().toLowerCase();

  while (label.endsWith(')')) {
    const openingParenthesis = label.lastIndexOf('(');
    if (openingParenthesis === -1) {
      break;
    }

    const suffix = label
      .slice(openingParenthesis + 1, -1)
      .replace(/^id\s*:\s*/i, '')
      .trim()
      .toLowerCase();
    if (suffix !== normalizedId) {
      break;
    }

    label = label.slice(0, openingParenthesis).trim();
  }

  return label;
};

/**
 * Formats a saved-search name for query-builder display.
 * API labels can already contain a trailing id in either `(id)` or
 * `(ID: id)` form; only the matching suffix is removed.
 */
export function formatSavedSearchDisplayLabel(
  text: string | undefined | null,
  id: string | number
): string {
  const idString = String(id).trim();
  const rawLabel = String(text ?? '').trim();
  const name = stripTrailingSavedSearchId(rawLabel, idString).trim();

  return `${name || rawLabel || idString} (ID: ${idString})`;
}

export function formatSavedSearchErrorLabel(rawId: string): string {
  return `(ID: ${rawId})`;
}

export function isSavedSearchesCriteria(value: unknown): value is string {
  return typeof value === 'string' && SAVED_SEARCHES_MARKER.test(value);
}

export function buildSavedSearchNameLookup(
  items?: readonly SavedSearchLookupItem[] | null
): Map<string, string> {
  const lookup = new Map<string, string>();

  for (const item of items ?? []) {
    if (
      !item ||
      item.isGroup ||
      item.id == null ||
      item.id === '' ||
      item.parent === '#'
    ) {
      continue;
    }
    lookup.set(String(item.id).trim(), String(item.text ?? ''));
  }

  return lookup;
}

const findMatchingBracket = (value: string, start: number): number => {
  let depth = 0;
  let inQuotes = false;
  let escaped = false;

  for (let index = start; index < value.length; index += 1) {
    const character = value.charAt(index);

    if (character === '\\' && inQuotes && !escaped) {
      escaped = true;
      continue;
    }

    if (character === '"' && !escaped) {
      inQuotes = !inQuotes;
    }
    escaped = false;

    if (inQuotes) {
      continue;
    }

    if (character === '[') {
      depth += 1;
    } else if (character === ']') {
      depth -= 1;
      if (depth === 0) {
        return index;
      }
    }
  }

  return -1;
};

const findSavedSearchesBlock = (
  value: string,
  fromIndex: number
): SavedSearchBlock | null => {
  const marker = /\bsavedSearches\s*:/gi;
  marker.lastIndex = fromIndex;
  const match = marker.exec(value);
  if (!match) {
    return null;
  }

  const bracketStart = value.indexOf('[', match.index + match[0].length);
  if (bracketStart === -1) {
    return {
      start: match.index,
      end: value.length - 1,
      inner: value.slice(match.index + match[0].length),
      malformed: true,
    };
  }

  const bracketEnd = findMatchingBracket(value, bracketStart);
  if (bracketEnd === -1) {
    return {
      start: match.index,
      end: value.length - 1,
      inner: value.slice(bracketStart),
      malformed: true,
    };
  }

  return {
    start: match.index,
    end: bracketEnd,
    inner: value.slice(bracketStart, bracketEnd + 1),
    malformed: false,
  };
};

const stripOuterBrackets = (value: string): string => {
  let stripped = value.trim();
  if (stripped.startsWith('[')) {
    stripped = stripped.slice(1);
  }
  if (stripped.endsWith(']')) {
    stripped = stripped.slice(0, -1);
  }
  return stripped.trim();
};

const splitCommaSeparatedItems = (value: string): string[] => {
  const items: string[] = [];
  let current = '';
  let inQuotes = false;
  let escaped = false;

  for (const character of value) {
    if (character === '\\' && inQuotes && !escaped) {
      escaped = true;
      current += character;
      continue;
    }

    if (character === '"' && !escaped) {
      inQuotes = !inQuotes;
    }
    escaped = false;

    if (character === ',' && !inQuotes) {
      items.push(current.trim());
      current = '';
      continue;
    }

    current += character;
  }

  items.push(current.trim());
  return items;
};

const stripWrappingQuotes = (value: string): string => {
  const trimmed = value.trim();
  if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
};

export function isStandaloneSavedSearchErrorToken(value: string): boolean {
  return STANDALONE_ERROR_TOKEN.test(stripWrappingQuotes(value));
}

const itemToRawId = (item: string): string => {
  const trimmed = stripWrappingQuotes(item);
  const idMatch = trimmed.match(TRAILING_ID_TOKEN);
  return idMatch ? idMatch[1].trim() : trimmed;
};

const parseSavedSearchItems = (innerOrList: string): ParsedSavedSearchItems => {
  const stripped = stripOuterBrackets(innerOrList);
  if (!stripped) {
    return {ids: [], malformed: true, executeForm: false};
  }

  const executeFormStart = stripped.match(/^ID\s*:\s*\[/i);
  if (executeFormStart) {
    const listStart = executeFormStart[0].length - 1;
    const listEnd = findMatchingBracket(stripped, listStart);
    if (listEnd === -1) {
      const parts = splitCommaSeparatedItems(stripped.slice(listStart + 1));
      return {
        ids: parts.filter(Boolean).map((item) => stripWrappingQuotes(item)),
        malformed: true,
        executeForm: true,
      };
    }

    const parts = splitCommaSeparatedItems(
      stripped.slice(listStart + 1, listEnd)
    );
    return {
      ids: parts.filter(Boolean).map((item) => stripWrappingQuotes(item)),
      malformed:
        stripped.slice(listEnd + 1).trim().length > 0 ||
        parts.some((part) => !part),
      executeForm: true,
    };
  }

  const malformedExecuteForm = stripped.match(/^ID\s*:\s*(.*)$/i);
  if (malformedExecuteForm) {
    const rawId = stripWrappingQuotes(malformedExecuteForm[1]);
    return {
      ids: rawId ? [rawId] : [],
      malformed: true,
      executeForm: true,
    };
  }

  const parts = splitCommaSeparatedItems(stripped);
  return {
    ids: parts.filter(Boolean).map(itemToRawId),
    malformed: parts.some((part) => !part),
    executeForm: false,
  };
};

/**
 * Extracts entered saved-search tokens without changing their raw text.
 * The parser accepts the executable form `savedSearches: [ ID: [1, 2] ]`.
 */
export function parseTypedSavedSearchIds(value: string): string[] {
  if (!value || !value.trim()) {
    return [];
  }

  if (!isSavedSearchesCriteria(value)) {
    const parsed = parseSavedSearchItems(value);
    return parsed.executeForm && !parsed.malformed ? parsed.ids : [];
  }

  const ids: string[] = [];
  let searchFrom = 0;

  while (searchFrom < value.length) {
    const block = findSavedSearchesBlock(value, searchFrom);
    if (!block) {
      break;
    }
    const parsed = parseSavedSearchItems(block.inner);
    if (!block.malformed && parsed.executeForm && !parsed.malformed) {
      ids.push(...parsed.ids);
    }
    searchFrom = block.end + 1;
  }

  return ids;
}

const rewriteSavedSearchesBlocks = (
  value: string,
  rewriteInnerItems: (inner: string, malformed: boolean) => string | null
): {value: string; changed: boolean} => {
  let result = value;
  let changed = false;
  let searchFrom = 0;

  while (searchFrom < result.length) {
    const block = findSavedSearchesBlock(result, searchFrom);
    if (!block) {
      break;
    }

    const rewrittenInner = rewriteInnerItems(block.inner, block.malformed);
    if (rewrittenInner == null) {
      searchFrom = block.end + 1;
      continue;
    }

    const replacement = `savedSearches: [ ${rewrittenInner} ]`;
    result =
      result.slice(0, block.start) + replacement + result.slice(block.end + 1);
    changed = true;
    searchFrom = block.start + replacement.length;
  }

  return {value: result, changed};
};

/**
 * Creates display-only saved-search syntax. The input value is never changed
 * by this helper; callers store the returned string in `Criteria.displayValue`.
 */
export function formatSavedSearchDisplayValue(
  value: string,
  lookup?: Map<string, string> | null,
  threshold?: number
): string | undefined {
  if (!value || !isSavedSearchesCriteria(value) || !lookup) {
    return undefined;
  }

  const formatted = rewriteSavedSearchesBlocks(
    value,
    (inner, blockMalformed) => {
      const parsed = parseSavedSearchItems(inner);
      if (!parsed.executeForm) {
        return null;
      }
      if (threshold !== undefined && parsed.ids.length >= threshold) {
        return null;
      }
      const ids = parsed.ids.length
        ? parsed.ids
        : [stripOuterBrackets(inner) || 'malformed'];
      const malformed = blockMalformed || parsed.malformed;

      const displayItems = ids.map((id) => {
        const isAccessibleNumericId =
          !malformed && NUMERIC_ID.test(id) && lookup.has(id);
        const isError = !isAccessibleNumericId;
        const label = isError
          ? formatSavedSearchErrorLabel(id)
          : formatSavedSearchDisplayLabel(lookup.get(id), id);

        return {
          isError,
          text: `"${label}"`,
        };
      });

      return [
        ...displayItems.filter((item) => !item.isError),
        ...displayItems.filter((item) => item.isError),
      ]
        .map((item) => item.text)
        .join(', ');
    }
  );

  return formatted.changed ? formatted.value : undefined;
}

/**
 * Returns whether any executable saved-search block has reached the configured
 * display-name conversion limit. Blocks are evaluated independently.
 */
export function hasSavedSearchBlockAtOrAboveThreshold(
  value: string,
  threshold: number
): boolean {
  if (!value || !isSavedSearchesCriteria(value)) {
    return false;
  }

  let searchFrom = 0;
  while (searchFrom < value.length) {
    const block = findSavedSearchesBlock(value, searchFrom);
    if (!block) {
      break;
    }

    const parsed = parseSavedSearchItems(block.inner);
    if (
      !block.malformed &&
      parsed.executeForm &&
      !parsed.malformed &&
      parsed.ids.length >= threshold
    ) {
      return true;
    }
    searchFrom = block.end + 1;
  }

  return false;
}

export function getInvalidSavedSearchIds(value: string): string[] {
  if (!value || !isSavedSearchesCriteria(value)) {
    return [];
  }

  const invalidIds: string[] = [];
  let searchFrom = 0;

  while (searchFrom < value.length) {
    const block = findSavedSearchesBlock(value, searchFrom);
    if (!block) {
      break;
    }

    const inner = stripOuterBrackets(block.inner);
    if (!/^ID\s*:\s*\[/i.test(inner)) {
      for (const item of splitCommaSeparatedItems(inner)) {
        if (isStandaloneSavedSearchErrorToken(item)) {
          invalidIds.push(itemToRawId(item));
        }
      }
    }

    searchFrom = block.end + 1;
  }

  return invalidIds;
}

export function hasInvalidSavedSearchTokens(value: string): boolean {
  return getInvalidSavedSearchIds(value).length > 0;
}

/**
 * Splits a display value into renderable text segments so only unresolved
 * saved-search tokens receive error styling.
 */
export function tokenizeSavedSearchChipValue(
  value: string
): SavedSearchChipToken[] | null {
  if (!hasInvalidSavedSearchTokens(value)) {
    return null;
  }

  const tokens: SavedSearchChipToken[] = [];
  const tokenPattern = /"(?:[^"\\]|\\.)*"|\(ID\s*:\s*[^)]+\)/gi;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = tokenPattern.exec(value))) {
    if (match.index > lastIndex) {
      tokens.push({
        text: value.slice(lastIndex, match.index),
        isError: false,
      });
    }

    const token = match[0];
    tokens.push({
      text: token,
      isError: isStandaloneSavedSearchErrorToken(token),
    });
    lastIndex = match.index + token.length;
  }

  if (lastIndex < value.length) {
    tokens.push({
      text: value.slice(lastIndex),
      isError: false,
    });
  }

  return tokens;
}
